import { ref, computed, type Ref } from 'vue'
import { peopleApi } from '@/plugins/api'

export interface FilterPreset {
  id: string
  name: string
  filters: {
    search?: string
    status?: string | null
    priority?: string | null
    tags?: string[]
    ownerIds?: string[]
    personId?: string | null // Deprecated: use personIds instead
    personIds?: string[]
    // Exclude (NOT) flags (legacy boolean — kept for backward compat)
    excludeTags?: boolean
    excludeOwnerIds?: boolean
    excludeProjectIds?: boolean
    excludePersonIds?: boolean
    // Excluded items per filter (new per-item granularity)
    excludedStatuses?: string[]
    excludedTags?: string[]
    excludedOwnerIds?: string[]
    excludedProjectIds?: string[]
    excludedPersonIds?: string[]
    // Project filter (for tasks)
    projectIds?: string[]
    // Idea-specific fields
    type?: string | null
    authorId?: string | null
    projectId?: string | null
    sortBy?: string
    sortOrder?: string
  }
  createdAt: string
}

// Shared state per settings key
interface PresetState {
  presets: Ref<FilterPreset[]>
  loading: Ref<boolean>
  loaded: Ref<boolean>
}

const stateMap = new Map<string, PresetState>()

function getState(settingsKey: string): PresetState {
  let state = stateMap.get(settingsKey)
  if (!state) {
    state = {
      presets: ref<FilterPreset[]>([]),
      loading: ref(false),
      loaded: ref(false),
    }
    stateMap.set(settingsKey, state)
  }
  return state
}

export function useFilterPresets(settingsKey = 'filterPresets') {
  const state = getState(settingsKey)

  // Load presets from user settings
  async function loadPresets() {
    if (state.loaded.value) return

    state.loading.value = true
    try {
      const response = await peopleApi.getMySettings()
      const settings = response.data?.settings || response.data || {}
      state.presets.value = settings[settingsKey] || []
      state.loaded.value = true
    } catch (e) {
      console.error('Failed to load filter presets:', e)
      state.presets.value = []
    } finally {
      state.loading.value = false
    }
  }

  // Save a new preset
  async function savePreset(name: string, filters: FilterPreset['filters']): Promise<FilterPreset | null> {
    const newPreset: FilterPreset = {
      id: crypto.randomUUID(),
      name,
      filters,
      createdAt: new Date().toISOString(),
    }

    const updatedPresets = [...state.presets.value, newPreset]

    try {
      await peopleApi.updateMySettings({ [settingsKey]: updatedPresets })
      state.presets.value = updatedPresets
      return newPreset
    } catch (e) {
      console.error('Failed to save filter preset:', e)
      return null
    }
  }

  // Update an existing preset
  async function updatePreset(id: string, name: string, filters: FilterPreset['filters']): Promise<boolean> {
    const index = state.presets.value.findIndex(p => p.id === id)
    if (index === -1) return false

    const updatedPresets = [...state.presets.value]
    updatedPresets[index] = {
      ...updatedPresets[index],
      name,
      filters,
    }

    try {
      await peopleApi.updateMySettings({ [settingsKey]: updatedPresets })
      state.presets.value = updatedPresets
      return true
    } catch (e) {
      console.error('Failed to update filter preset:', e)
      return false
    }
  }

  // Delete a preset
  async function deletePreset(id: string): Promise<boolean> {
    const updatedPresets = state.presets.value.filter(p => p.id !== id)

    try {
      await peopleApi.updateMySettings({ [settingsKey]: updatedPresets })
      state.presets.value = updatedPresets
      return true
    } catch (e) {
      console.error('Failed to delete filter preset:', e)
      return false
    }
  }

  // Get preset by ID
  function getPreset(id: string): FilterPreset | undefined {
    return state.presets.value.find(p => p.id === id)
  }

  return {
    presets: computed(() => state.presets.value),
    loading: computed(() => state.loading.value),
    loadPresets,
    savePreset,
    updatePreset,
    deletePreset,
    getPreset,
  }
}
